import React from "react";
import { Box, Button } from "@mui/material";
import { COLORS, EsgSingleSelectControlledField } from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { getFiscalYear } from "utils";
import { LOCAL_STORAGE_KEYS } from "consts";
import { MissingDocumentsFiltersBarProps } from "./types";

export const MissingDocumentsFiltersBar = ({
  allFiscalYears,
  countries,
  utilityTypes,
  sites,
  businessUnits,
  countryRegions,
  companyMonths,
  currentFY,
  setCurrentFY,
  currentCountries,
  currentSiteIds,
  currentUtilityTypeIds,
  currentBusinessUnits,
  currentCountryRegions,
  currentStatus,
  allStatusOptions,
  areSitesFiltered,
  getSitesFiltered,
  countrySetSelectedValues,
  countryOnChange,
  utilityTypeSetSelectedValues,
  utilityTypeOnChange,
  sitesSetSelectedValues,
  sitesOnChange,
  businessUnitSetSelectedValues,
  businessUnitOnChange,
  regionSetSelectedValues,
  regionOnChange,
  statusSetSelectedValues,
  statusOnChange,
  enableSearch,
  searchActive,
  onlyMissing,
  setOnlyMissing,
  setSearchActive,
  resetFilterState,
  defaultObject,
  handleUpdateMissingDocuments,
  setColumnFilters,
  resetSelection,
  resetPage,
  size,
}: MissingDocumentsFiltersBarProps) => {
  const handleClearFilters = () => {
    resetFilterState();
    setColumnFilters([]);
    resetSelection();
    resetPage();
    handleUpdateMissingDocuments({ ...defaultObject.current, page: 0, size });
  };

  const onOnlyMissingButtonClick = () => {
    setOnlyMissing(true);
    resetPage();
    handleUpdateMissingDocuments({ ...defaultObject.current, page: 0, size });
  };

  const onShowAllButtonClick = () => {
    setOnlyMissing(false);
    resetPage();
    handleUpdateMissingDocuments({ ...defaultObject.current, page: 0, size });
  };

  const onFiscalYearChange = (value: string | number) => {
    const fyValue = value as string;
    setCurrentFY(fyValue);
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_FISCAL_YEAR,
      fyValue,
    );
    const { fromDate, endDate } = getFiscalYear(
      companyMonths,
      fyValue.substring(0, 4),
    );
    // eslint-disable-next-line no-param-reassign
    defaultObject.current = {
      ...defaultObject.current,
      fromDate,
      toDate: endDate,
    };
    setSearchActive(true);
  };

  const onSearchClick = () => {
    resetPage();
    handleUpdateMissingDocuments({ ...defaultObject.current, page: 0, size });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <Box sx={{ width: "200px" }}>
          <EsgSingleSelectControlledField
            label="Fiscal Year"
            placeholder="Select Fiscal Year"
            options={allFiscalYears.map((year) => ({
              label: year,
              value: year,
            }))}
            selectedValue={currentFY}
            setSelectedValue={onFiscalYearChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Country"
            selectedValues={currentCountries}
            setSelectedValues={countrySetSelectedValues}
            options={countries}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={countryOnChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Utility Type"
            selectedValues={currentUtilityTypeIds}
            setSelectedValues={utilityTypeSetSelectedValues}
            options={utilityTypes}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={utilityTypeOnChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Sites"
            selectedValues={currentSiteIds}
            setSelectedValues={sitesSetSelectedValues}
            options={!areSitesFiltered ? sites : getSitesFiltered()}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={sitesOnChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Business Unit"
            selectedValues={currentBusinessUnits}
            setSelectedValues={businessUnitSetSelectedValues}
            options={businessUnits}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={businessUnitOnChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Region"
            selectedValues={currentCountryRegions}
            setSelectedValues={regionSetSelectedValues}
            options={countryRegions}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={regionOnChange}
          />
        </Box>
        <Box sx={{ width: "200px" }}>
          <EsgMultiselectControlledField
            label="Status"
            selectedValues={currentStatus}
            setSelectedValues={statusSetSelectedValues}
            options={allStatusOptions}
            type="checkMissing"
            isAllSelectedOnEmptySelection
            onchangeFn={statusOnChange}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              sx={{
                width: "140px",
                height: "36px",
                borderRadius: "8px",
                transition: "none",
              }}
              disabled={!enableSearch}
              variant={searchActive ? "contained" : "outlined"}
              onClick={onSearchClick}
            >
              Search
            </Button>
            <Button
              sx={{ width: "140px", height: "36px", borderRadius: "8px" }}
              variant="outlined"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
          <Box
            sx={{
              minWidth: "fit-content",
              height: "36px",
              padding: "3px",
              border: `1px solid ${COLORS.Mainblue}`,
              borderRadius: "30px",
            }}
          >
            <Button
              variant={onlyMissing ? "contained" : "text"}
              sx={{ borderRadius: "30px", height: "100%" }}
              onClick={onOnlyMissingButtonClick}
            >
              Only Missing
            </Button>
            <Button
              variant={!onlyMissing ? "contained" : "text"}
              sx={{ borderRadius: "30px", height: "100%" }}
              onClick={onShowAllButtonClick}
            >
              Show all
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
